(() => {
  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }

    callback();
  };

  const checkoutRoot = () => document.querySelector('.event-checkout-page');

  const updateCartCount = (quantity) => {
    if (quantity === null || quantity === undefined) {
      return;
    }

    document.querySelectorAll('[data-event-cart-count]').forEach((badge) => {
      badge.textContent = String(quantity);
      badge.hidden = Number.parseInt(String(quantity), 10) <= 0;
    });
  };

  const updateCsrf = (params) => {
    if (!params || !params.csrf_token_name || !params.csrf_token_value) {
      return;
    }

    document.querySelectorAll(`input[name="${params.csrf_token_name}"]`).forEach((input) => {
      input.value = params.csrf_token_value;
    });
  };

  const showCartDrawer = () => {
    const drawer = document.getElementById('eventCartDrawer');
    if (!drawer || !window.bootstrap) {
      return;
    }

    window.bootstrap.Offcanvas.getOrCreateInstance(drawer).show();
  };

  const loadCartDrawer = async (toggle, openDrawer = false) => {
    const summaryUrl = toggle && toggle.getAttribute('data-cart-summary-url');
    const body = document.querySelector('[data-event-cart-drawer-body]');
    if (!summaryUrl || !body) {
      return;
    }

    const root = checkoutRoot();
    const loadError = root?.getAttribute('data-i18n-cart-load-error') || 'The cart could not be loaded.';

    try {
      const response = await fetch(summaryUrl, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`cart_summary_${response.status}`);
      }

      const params = await response.json();
      updateCsrf(params);
      if (params.html) {
        body.innerHTML = params.html;
      }
      if (params.summary) {
        updateCartCount(params.summary.quantity);
      }
      if (openDrawer) {
        showCartDrawer();
      }
    } catch (error) {
      body.innerHTML = `<div class="alert alert-warning mb-0">${loadError}</div>`;
      if (openDrawer) {
        showCartDrawer();
      }
    }
  };

  const initCartDrawer = () => {
    const firstToggle = document.querySelector('[data-event-cart-toggle]');
    let quantityUpdatePending = false;
    const queuedQuantities = new Map();
    if (firstToggle) {
      loadCartDrawer(firstToggle, false);
    }

    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-event-cart-toggle]');
      if (!toggle) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      loadCartDrawer(toggle, true);
    }, true);

    const processQuantityQueue = async () => {
      if (quantityUpdatePending || queuedQuantities.size === 0) {
        return;
      }

      quantityUpdatePending = true;
      try {
        while (queuedQuantities.size > 0) {
          const [cartHash, nextQuantity] = queuedQuantities.entries().next().value;
          queuedQuantities.delete(cartHash);

          const items = document.querySelector('.event-cart-drawer__items');
          const updateUrl = items?.getAttribute('data-cart-quantity-url');
          const csrf = items?.querySelector('input[type="hidden"][name]');
          if (!updateUrl || !csrf?.name || !csrf.value) {
            queuedQuantities.clear();
            return;
          }

          const formData = new FormData();
          formData.set(`quantities[${cartHash}]`, String(nextQuantity));
          formData.set(csrf.name, csrf.value);

          const response = await fetch(updateUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              'X-CSRF-TOKEN': csrf.value,
            },
            body: formData,
          });
          const params = await response.json().catch(() => ({}));
          updateCsrf(params);
          const toggle = document.querySelector('[data-event-cart-toggle]');
          await loadCartDrawer(toggle, true);
          if (!response.ok) {
            queuedQuantities.clear();
            return;
          }
        }
      } catch (error) {
        queuedQuantities.clear();
        const toggle = document.querySelector('[data-event-cart-toggle]');
        await loadCartDrawer(toggle, true);
      } finally {
        quantityUpdatePending = false;
        if (queuedQuantities.size > 0) {
          processQuantityQueue();
        }
      }
    };

    document.addEventListener('click', (event) => {
      const control = event.target.closest('[data-drawer-step], [data-drawer-remove]');
      if (!control) {
        return;
      }

      const row = control.closest('[data-drawer-row]');
      const quantity = row?.querySelector('[data-drawer-qty]');
      const cartHash = row?.getAttribute('data-cart-hash');
      if (!cartHash || !quantity) {
        return;
      }

      const currentQuantity = queuedQuantities.has(cartHash)
        ? queuedQuantities.get(cartHash)
        : (Number.parseInt(quantity.textContent || '0', 10) || 0);
      const step = Number.parseInt(control.getAttribute('data-drawer-step') || '0', 10) || 0;
      const nextQuantity = control.matches('[data-drawer-remove]') ? 0 : Math.max(0, currentQuantity + step);
      queuedQuantities.set(cartHash, nextQuantity);
      quantity.textContent = String(nextQuantity);
      processQuantityQueue();
    });
  };

  const initCustomerTypeFields = () => {
    const form = document.querySelector('[data-checkout-form]');
    if (!form) {
      return;
    }

    const customerType = form.querySelector('#customer_type');
    const taxField = form.querySelector('[data-tax-number-field]');
    const taxInput = form.querySelector('#billing_tax_number');
    const billingNameLabel = form.querySelector('[data-billing-name-label]');
    if (!customerType) {
      return;
    }

    const sync = () => {
      const isCompany = customerType.value === 'COMPANY';
      if (taxField) {
        taxField.hidden = !isCompany;
        taxField.classList.toggle('d-none', !isCompany);
      }
      if (taxInput) {
        taxInput.required = isCompany;
      }
      if (billingNameLabel) {
        billingNameLabel.textContent = isCompany
          ? billingNameLabel.getAttribute('data-company-label')
          : billingNameLabel.getAttribute('data-person-label');
      }
    };

    customerType.addEventListener('change', sync);
    sync();
  };

  const initPostcodeCityLookup = () => {
    const root = checkoutRoot();
    const postcode = document.getElementById('billing_postcode');
    const city = document.getElementById('billing_city');
    const country = document.getElementById('billing_country');
    const lookupBaseUrl = root?.getAttribute('data-postcode-city-url');
    if (!postcode || !city || !country || !lookupBaseUrl) {
      return;
    }

    let abortController = null;

    city.addEventListener('input', () => {
      city.dataset.autofilledCity = 'false';
    });

    const lookup = async () => {
      const normalizedPostcode = postcode.value.trim();
      if (country.value !== 'HU' || !/^\d{4}$/.test(normalizedPostcode)) {
        return;
      }
      if (city.value.trim() !== '' && city.dataset.autofilledCity !== 'true') {
        return;
      }

      abortController?.abort();
      abortController = new AbortController();

      try {
        const separator = lookupBaseUrl.includes('?') ? '&' : '?';
        const response = await fetch(`${lookupBaseUrl}/${encodeURIComponent(normalizedPostcode)}${separator}is_ajax=1`, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          signal: abortController.signal,
        });
        if (!response.ok) {
          return;
        }

        const params = await response.json();
        const cityName = typeof params.html === 'string' ? params.html.trim() : '';
        if (params.message === 'ok' && cityName !== '') {
          city.value = cityName;
          city.dataset.autofilledCity = 'true';
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          city.dataset.autofilledCity = 'false';
        }
      }
    };

    postcode.addEventListener('input', lookup);
    postcode.addEventListener('blur', lookup);
    country.addEventListener('change', lookup);
    lookup();
  };

  // Lets the buyer copy the first ticket's personalization fields to every
  // other ticket of the same type instead of retyping the same details.
  const initTicketCopyButtons = () => {
    document.querySelectorAll('[data-copy-ticket-data]').forEach((button) => {
      button.addEventListener('click', () => {
        const hash = button.getAttribute('data-copy-ticket-data');
        if (!hash) {
          return;
        }

        const selector = `[data-ticket-card][data-ticket-hash="${CSS.escape(hash)}"]`;
        const sourceCard = document.querySelector(`${selector}[data-ticket-index="0"]`);
        if (!sourceCard) {
          return;
        }

        document.querySelectorAll(selector).forEach((targetCard) => {
          if (targetCard === sourceCard) {
            return;
          }

          const targetIndex = targetCard.getAttribute('data-ticket-index');
          sourceCard.querySelectorAll('input, select').forEach((sourceField) => {
            const targetName = sourceField.name.replace(`[${hash}][0][`, `[${hash}][${targetIndex}][`);
            const targetFields = targetCard.querySelectorAll(`[name="${CSS.escape(targetName)}"]`);
            if (!targetFields.length) {
              return;
            }

            if (sourceField.type === 'checkbox') {
              targetFields.forEach((targetField) => {
                if (targetField.value === sourceField.value) {
                  targetField.checked = sourceField.checked;
                }
              });
            } else {
              targetFields[0].value = sourceField.value;
            }
          });

          if (typeof targetCard.open !== 'undefined') {
            targetCard.open = true;
          }
        });
      });
    });
  };

  // Deferred ticket personalization stays on the same page while a ticket is
  // saved or sent. The server still handles the normal form POST when
  // JavaScript is unavailable, but an interactive page never needs to reload
  // after one of these actions.
  //
  // A ticket can only be stored once every required field is filled in, so the
  // client blocks a save that cannot succeed and the server rejects incomplete
  // tickets on its own. Whether a ticket counts as complete always comes from
  // the server response, never from the client-side check. The ticket sale
  // status (PENDING/PAID/OUTSIDE) is never derived or altered here - it only
  // decides whether sending is offered.
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const PERSONALIZATION_RESULT_PARAMS = [
    'saved',
    'saved_ticket',
    'saved_partial',
    'save_blocked',
    'save_error',
    'sent',
    'send_failed',
  ];

  const initPersonalizationForm = () => {
    const page = document.querySelector('[data-personalization-page]');
    const alerts = page?.querySelector('[data-personalization-alerts]');
    const form = document.querySelector('[data-personalization-form]');
    if (!form || !page || !alerts) {
      return;
    }

    let pending = false;
    const text = (key, fallback) => page.getAttribute(`data-i18n-personalization-${key}`) || fallback;

    // A reload must not replay the redirect-based result message of an earlier
    // no-JavaScript submit.
    const dropResultParamsFromUrl = () => {
      const url = new URL(window.location.href);
      if (!PERSONALIZATION_RESULT_PARAMS.some((param) => url.searchParams.has(param))) {
        return;
      }

      PERSONALIZATION_RESULT_PARAMS.forEach((param) => url.searchParams.delete(param));
      window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    };

    const showAlerts = (messages) => {
      alerts.replaceChildren();
      const visible = messages.filter(Boolean);
      visible.forEach(({ type, message }) => {
        const alert = document.createElement('div');
        alert.className = `alert alert-${type}`;
        alert.setAttribute('role', 'status');
        alert.tabIndex = -1;
        alert.textContent = message;
        alerts.appendChild(alert);
      });

      const first = alerts.firstElementChild;
      if (first) {
        first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        first.focus({ preventScroll: true });
      }
    };

    const ticketCards = () => Array.from(form.querySelectorAll('[data-ticket-card]'));

    const fieldError = (field) => {
      const errorId = field.getAttribute('aria-describedby');
      return errorId ? document.getElementById(errorId) : null;
    };

    const setFieldError = (field, message) => {
      const target = fieldError(field);
      if (target) {
        target.textContent = message || '';
        target.classList.toggle('d-block', Boolean(message));
      }

      if (field.matches('input, select, textarea')) {
        field.classList.toggle('is-invalid', Boolean(message));
      }

      if (message) {
        field.setAttribute('aria-invalid', 'true');
      } else {
        field.removeAttribute('aria-invalid');
      }
    };

    const requiredFields = (card) => Array.from(card.querySelectorAll('[data-personalization-required="1"]'));

    const ticketRequiresData = (card) => requiredFields(card).length > 0;

    // Returns the fields that still need attention without preventing a save.
    const invalidFields = (card) => {
      const missing = [];
      requiredFields(card).forEach((field) => {
        if (field.matches('[data-personalization-group="checkbox"]')) {
          if (!field.querySelector('input[type="checkbox"]:checked')) {
            missing.push({ field, message: text('field-required', 'This field is required.') });
          }
          return;
        }

        if (field.disabled || field.readOnly) {
          return;
        }

        const value = String(field.value || '').trim();
        if (value === '') {
          missing.push({ field, message: text('field-required', 'This field is required.') });
          return;
        }

        if (field.type === 'email' && !EMAIL_PATTERN.test(value)) {
          missing.push({ field, message: text('field-invalid-email', 'Please enter a valid email address.') });
        }
      });

      return missing;
    };

    const showFieldErrors = (card) => {
      const invalid = new Map(invalidFields(card).map(({ field, message }) => [field, message]));
      requiredFields(card).forEach((field) => {
        setFieldError(field, invalid.get(field) || '');
      });

      return invalid.size === 0;
    };

    const clearFieldError = (field) => {
      const owner = field.closest('[data-personalization-required="1"]') || field;
      if (owner.matches('[data-personalization-required="1"]')) {
        setFieldError(owner, '');
      }
    };

    const toggleBadge = (card, selector, visible) => {
      const badge = card.querySelector(selector);
      if (badge) {
        badge.hidden = !visible;
      }
    };

    const toggleHint = (card, name, visible) => {
      const hint = card.querySelector(`[data-ticket-hint="${name}"]`);
      if (hint) {
        hint.hidden = !visible;
      }
    };

    const updatePersonSummary = (card) => {
      const lastName = card.querySelector('[name$="[last_name]"]')?.value.trim() || '';
      const firstName = card.querySelector('[name$="[first_name]"]')?.value.trim() || '';
      const email = card.querySelector('input[type="email"]')?.value.trim() || '';
      const summary = [lastName && firstName ? `${lastName} ${firstName}` : (lastName || firstName), email]
        .filter(Boolean)
        .join(' · ');
      const summaryElement = card.querySelector('[data-ticket-person-summary]');
      if (summaryElement) {
        summaryElement.textContent = summary || text('not-filled', 'Not filled in');
      }
    };

    const updateProgress = () => {
      const badge = page.querySelector('[data-personalization-progress]');
      if (!badge) {
        return;
      }

      const template = text('progress', '{filled} / {total}');
      const cards = ticketCards();
      const total = Number.parseInt(badge.getAttribute('data-progress-total') || '', 10) || cards.length;
      const filled = cards.filter((card) => card.dataset.ticketComplete === '1').length;
      badge.textContent = template
        .replace('{filled}', String(filled))
        .replace('{total}', String(total));
      badge.classList.toggle('personalization-progress--done', total > 0 && filled >= total);
    };

    const updateCardState = (card) => {
      const isComplete = card.dataset.ticketComplete === '1';
      const isDirty = card.dataset.ticketDirty === '1';
      const isUsed = card.classList.contains('personalization-ticket--used');
      const needsData = !isComplete && !isUsed && ticketRequiresData(card);

      card.classList.toggle('personalization-ticket--complete', isComplete);
      card.classList.toggle('personalization-ticket--dirty', isDirty);
      toggleBadge(card, '[data-ticket-complete]', isComplete);
      toggleBadge(card, '[data-ticket-incomplete]', needsData);
      toggleBadge(card, '[data-ticket-unsaved]', isDirty);
    };

    const updateSendAvailability = () => {
      const paidOrder = page.getAttribute('data-personalization-paid') === '1';

      ticketCards().forEach((card) => {
        const isUsed = card.classList.contains('personalization-ticket--used');
        const emailField = card.querySelector('input[type="email"]');
        const emailHint = card.querySelector('[data-ticket-hint-email]');
        // Tickets whose group only asks for extra fields have no editable email
        // input, so the server-rendered ticket email stays authoritative.
        const email = (emailField ? emailField.value : (emailHint?.textContent || '')).trim();
        const validEmail = EMAIL_PATTERN.test(email);
        const saved = card.dataset.ticketSaved === '1' && card.dataset.ticketDirty !== '1';
        const canSend = saved && card.dataset.ticketCanSendAfterSave === '1' && validEmail;

        const sendButton = card.querySelector('[name="send_ticket"]');
        if (sendButton) {
          sendButton.disabled = !canSend;
        }

        if (emailField) {
          if (emailHint) {
            emailHint.textContent = email;
          }
          toggleHint(card, 'email', email !== '');
          toggleHint(card, 'email-missing', email === '');
        }

        if (!isUsed) {
          toggleHint(card, 'save', !saved);
          toggleHint(card, 'payment', saved && !paidOrder);
          toggleHint(card, 'email-invalid', saved && paidOrder && !validEmail);
        }
      });

      // Checked-in tickets are read-only and never part of a send action, so
      // they must not keep the bulk actions disabled.
      const cards = ticketCards().filter((card) => !card.classList.contains('personalization-ticket--used'));
      const allSaved = cards.length > 0
        && cards.every((card) => card.dataset.ticketSaved === '1' && card.dataset.ticketDirty !== '1');
      const sendAll = form.querySelector('[name="action"][value="send_all"]');
      if (sendAll) {
        sendAll.disabled = !paidOrder || !allSaved;
      }

      const saveHint = form.querySelector('[data-bulk-hint="save"]');
      if (saveHint) {
        saveHint.hidden = allSaved;
      }
      const paymentHint = form.querySelector('[data-bulk-hint="payment"]');
      if (paymentHint) {
        paymentHint.hidden = !(allSaved && !paidOrder);
      }
    };

    const markDirty = (event) => {
      const card = event.target.closest('[data-ticket-card]');
      if (!card) {
        return;
      }

      updatePersonSummary(card);
      clearFieldError(event.target);
      card.dataset.ticketDirty = '1';
      updateCardState(card);
      updateSendAvailability();
    };

    const setBusyState = (button, label) => {
      const original = Array.from(button.childNodes);
      const spinner = document.createElement('span');
      spinner.className = 'spinner-border spinner-border-sm me-1';
      spinner.setAttribute('aria-hidden', 'true');
      button.replaceChildren(spinner, document.createTextNode(label));

      return () => {
        button.replaceChildren(...original);
      };
    };

    // Copies the first editable ticket onto the ones that are not complete yet.
    // Already complete tickets are skipped so an accidental click cannot
    // overwrite a participant who was filled in earlier. Nothing is persisted
    // until the buyer saves.
    const copyFirstTicket = () => {
      const cards = ticketCards().filter((card) => !card.classList.contains('personalization-ticket--used'));
      const source = cards[0];
      const targets = cards.slice(1).filter((card) => card.dataset.ticketComplete !== '1');
      if (!source || targets.length === 0) {
        return;
      }

      const sourceFields = Array.from(source.querySelectorAll('input:not([type="hidden"]), select'));
      targets.forEach((target) => {
        const targetId = target.getAttribute('data-ticket-id');
        sourceFields.forEach((sourceField) => {
          const suffix = String(sourceField.name || '').match(/^tickets\[\d+\](.+)$/);
          if (!suffix) {
            return;
          }

          const targetFields = Array.from(
            target.querySelectorAll(`[name="${CSS.escape(`tickets[${targetId}]${suffix[1]}`)}"]`)
          );
          if (targetFields.length === 0) {
            return;
          }

          if (sourceField.type === 'checkbox') {
            targetFields.forEach((targetField) => {
              if (targetField.value === sourceField.value) {
                targetField.checked = sourceField.checked;
              }
            });
            return;
          }

          targetFields[0].value = sourceField.value;
        });

        target.open = true;
        target.dataset.ticketDirty = '1';
        requiredFields(target).forEach((field) => setFieldError(field, ''));
        updatePersonSummary(target);
        updateCardState(target);
      });

      updateSendAvailability();
    };

    const copyButton = form.querySelector('[data-personalization-copy]');
    if (copyButton) {
      copyButton.hidden = false;
      copyButton.addEventListener('click', copyFirstTicket);
    }

    form.addEventListener('input', markDirty);
    form.addEventListener('change', markDirty);

    window.addEventListener('beforeunload', (event) => {
      if (pending || !ticketCards().some((card) => card.dataset.ticketDirty === '1')) {
        return;
      }

      event.preventDefault();
      event.returnValue = text('unsaved-leave', '');
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (pending) {
        return;
      }

      const submitter = event.submitter;
      const action = submitter?.getAttribute('data-personalization-action') || 'save_all';
      const singleTicketCard = submitter?.closest('[data-ticket-card]') || null;
      const affectedCards = (singleTicketCard ? [singleTicketCard] : ticketCards())
        .filter((card) => !card.classList.contains('personalization-ticket--used'));
      const incompleteCards = affectedCards.filter((card) => !showFieldErrors(card));

      // Nothing could be stored, so the request is not worth sending: the server
      // would reject every affected ticket anyway.
      if (incompleteCards.length === affectedCards.length && incompleteCards.length > 0) {
        showAlerts([{ type: 'danger', message: text('save-blocked', 'Please fill in every required field first.') }]);
        incompleteCards[0].open = true;
        incompleteCards[0].querySelector('.is-invalid')?.focus();
        return;
      }

      pending = true;
      const originalStates = Array.from(form.querySelectorAll('button')).map((button) => ({
        button,
        disabled: button.disabled,
      }));
      originalStates.forEach(({ button }) => {
        button.disabled = true;
      });
      const restoreLabel = submitter
        ? setBusyState(
          submitter,
          action === 'send' || action === 'send_all'
            ? text('sending', 'Sending…')
            : text('saving', 'Saving…')
        )
        : null;

      const formData = new FormData(form);
      if (submitter?.name) {
        formData.set(submitter.name, submitter.value);
      }

      const csrfField = form.querySelector('input[type="hidden"][name]:not([name^="tickets"])');
      const headers = {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      };
      if (csrfField?.value) {
        headers['X-CSRF-TOKEN'] = csrfField.value;
      }

      try {
        const response = await fetch(form.getAttribute('action'), {
          method: 'POST',
          credentials: 'same-origin',
          headers,
          body: formData,
        });
        const payload = await response.json().catch(() => ({}));
        updateCsrf(payload);

        const markIncomplete = (ids) => {
          const incompleteIds = new Set((ids || []).map((id) => String(id)));
          ticketCards()
            .filter((card) => incompleteIds.has(card.getAttribute('data-ticket-id')))
            .forEach((card) => {
              card.dataset.ticketComplete = '0';
              showFieldErrors(card);
              updateCardState(card);
            });
        };

        if (payload.message === 'incomplete') {
          markIncomplete(payload.incomplete_ticket_ids);
          showAlerts([{ type: 'danger', message: text('save-blocked', 'Please fill in every required field first.') }]);
          return;
        }

        if (!response.ok || payload.message !== 'ok') {
          showAlerts([{ type: 'danger', message: text('save-error', 'The ticket details could not be saved. Please try again.') }]);
          return;
        }

        const savedIds = new Set((payload.saved_ticket_ids || []).map((id) => String(id)));
        const completeIds = new Set((payload.complete_ticket_ids || []).map((id) => String(id)));

        ticketCards().forEach((card) => {
          const ticketId = card.getAttribute('data-ticket-id');
          const wasSaved = savedIds.has(ticketId);
          if (wasSaved) {
            card.dataset.ticketSaved = '1';
            card.dataset.ticketDirty = '0';
          }
          if (completeIds.has(ticketId)) {
            card.dataset.ticketComplete = '1';
          }

          updateCardState(card);
          if (wasSaved) {
            // Re-check only the cards this action saved: a filled field loses
            // its marker, while a stored but unusable value - a malformed email
            // address, for example - stays visible.
            showFieldErrors(card);
          }
        });
        markIncomplete(payload.incomplete_ticket_ids);

        const messages = [];
        if (payload.saved_partial) {
          messages.push({ type: 'warning', message: text('saved-partial', 'Only the fully filled tickets were saved.') });
        } else if (action === 'save' || action === 'save_all' || payload.saved || payload.saved_ticket) {
          messages.push({ type: 'success', message: text('saved', 'Ticket details saved.') });
        }
        if (payload.sent) {
          messages.push({ type: 'success', message: text('sent', 'The saved tickets were sent successfully.') });
        }
        if (payload.send_failed) {
          messages.push({ type: 'warning', message: text('send-failed', 'Some tickets could not be sent. Check the ticket email address and payment status.') });
        }
        showAlerts(messages);
      } catch (error) {
        showAlerts([{ type: 'danger', message: text('save-error', 'The ticket details could not be saved. Please try again.') }]);
      } finally {
        originalStates.forEach(({ button, disabled }) => {
          button.disabled = disabled;
        });
        if (restoreLabel) {
          restoreLabel();
        }
        pending = false;
        updateSendAvailability();
        updateProgress();
      }
    });

    dropResultParamsFromUrl();
    ticketCards().forEach((card) => {
      card.dataset.ticketSaved = card.dataset.ticketSaved || '0';
      card.dataset.ticketDirty = card.dataset.ticketDirty || '0';
      card.dataset.ticketComplete = card.querySelector('[data-ticket-complete]')?.hidden === false ? '1' : '0';
      updatePersonSummary(card);
      updateCardState(card);
    });
    updateSendAvailability();
    updateProgress();
  };

  // Validates the billing step over fetch instead of a full-page POST, so an
  // invalid submission re-renders inline instead of round-tripping through a
  // page reload; a valid submission navigates on to the summary step.
  const initCheckoutBillingForm = () => {
    const form = document.querySelector('[data-checkout-form]');
    if (!form) {
      return;
    }

    const root = checkoutRoot();
    const summaryBanner = form.querySelector('[data-checkout-errors-summary]');
    const paymentBanner = form.querySelector('[data-checkout-payment-unavailable]');
    const requestBanner = form.querySelector('[data-checkout-request-error]');
    const reloadButton = requestBanner?.querySelector('[data-checkout-reload]');
    reloadButton?.addEventListener('click', () => window.location.reload());
    const i18n = (key) => root?.getAttribute(`data-i18n-${key}`) || '';

    // Server-rendered errors (no-JS submit or redirect): tie each to its field and focus the first.
    form.querySelectorAll('[data-field-error]').forEach((feedback) => {
      const input = feedback.previousElementSibling;
      if (!input || !input.matches('input, select, textarea')) {
        return;
      }
      feedback.id = feedback.id || `${input.id}-error`;
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', feedback.id);
    });
    form.querySelector('[aria-invalid="true"]')?.focus();

    const clearErrors = () => {
      form.querySelectorAll('.is-invalid').forEach((el) => {
        el.classList.remove('is-invalid');
        el.removeAttribute('aria-invalid');
        el.removeAttribute('aria-describedby');
      });
      form.querySelectorAll('[data-field-error]').forEach((el) => el.remove());
      if (summaryBanner) {
        summaryBanner.hidden = true;
      }
      if (paymentBanner) {
        paymentBanner.hidden = true;
      }
      if (requestBanner) {
        requestBanner.hidden = true;
      }
    };

    // Audit UX-003: an expired session asks for a reload, any other failure for a retry;
    // only a real payment-method refusal shows the payment banner.
    const showRequestError = (sessionExpired) => {
      if (!requestBanner) {
        return;
      }
      requestBanner.querySelector('[data-checkout-request-error-text]').textContent = i18n(sessionExpired ? 'session-expired' : 'request-failed');
      if (reloadButton) {
        reloadButton.hidden = !sessionExpired;
      }
      requestBanner.hidden = false;
    };

    const showFieldErrors = (errors) => {
      Object.keys(errors || {}).forEach((field) => {
        const input = form.querySelector(`[name="${field}"]`);
        if (!input) {
          return;
        }

        input.classList.add('is-invalid');
        const reason = String(errors[field] || '').replace(/_/g, '-');
        const message = i18n(`validation-${reason}`) || i18n('validation-invalid');
        const feedback = document.createElement('div');
        feedback.className = 'invalid-feedback d-block';
        feedback.setAttribute('data-field-error', '');
        feedback.id = `${input.id || input.name.replace(/[^a-z0-9_-]/gi, '_')}-error`;
        feedback.textContent = message;
        input.insertAdjacentElement('afterend', feedback);
        // Audit UX-004: the message belongs to the field for assistive technology.
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', feedback.id);
      });

      if (summaryBanner) {
        summaryBanner.hidden = false;
      }
      form.querySelector('[aria-invalid="true"]')?.focus();
    };

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      clearErrors();

      const csrfInput = form.querySelector('input[name]');
      const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
      if (csrfInput && csrfInput.value) {
        headers['X-CSRF-TOKEN'] = csrfInput.value;
      }

      fetch(form.getAttribute('action'), {
        method: 'POST',
        headers,
        credentials: 'same-origin',
        body: new FormData(form),
      })
        .then(async (response) => ({ ok: response.ok, status: response.status, payload: await response.json().catch(() => ({})) }))
        .then(({ ok, status, payload }) => {
          updateCsrf(payload);

          if (ok && payload && payload.redirect_url) {
            window.location.href = payload.redirect_url;
            return;
          }

          if (payload && payload.message === 'billing_invalid') {
            showFieldErrors(payload.errors);
            return;
          }

          if (payload && payload.message === 'payment_method_unavailable' && paymentBanner) {
            paymentBanner.hidden = false;
            return;
          }

          showRequestError(status === 403 || (payload && payload.message === 'csrf_error'));
        })
        .catch(() => {
          // Network failure: fall back to a real submit (bypasses the submit
          // event entirely) so the buyer is not stuck on a silent button.
          form.submit();
        });
    });
  };

  const initPaymentCompletionUrl = () => {
    const page = document.querySelector('[data-payment-completion-url]');
    const url = page?.getAttribute('data-payment-completion-url')?.trim();
    const fallback = page?.querySelector('[data-payment-completion-link]');
    if (!page || !url) {
      return;
    }

    const orderKey = page.getAttribute('data-payment-completion-key') || window.location.pathname;
    const storageKey = `rem-payment-completion:${window.location.origin}:${orderKey}:${url}`;
    try {
      if (window.sessionStorage.getItem(storageKey) === '1') {
        if (fallback) {
          fallback.hidden = true;
        }
        return;
      }
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browser contexts.
    }

    let popup = null;
    try {
      popup = window.open(url, '_blank');
      if (popup) {
        popup.opener = null;
      }
    } catch (error) {
      // Show the manual link below when the browser rejects the popup.
    }

    if (popup) {
      if (fallback) {
        fallback.hidden = true;
      }
      try {
        window.sessionStorage.setItem(storageKey, '1');
      } catch (error) {
        // The link was opened; no persistent marker is available in this context.
      }
    }
  };

  // Audit UX-001: a [data-submit-lock] form that really navigates (no JS handler took it over)
  // is sent once; a double tap on a slow mobile connection no longer creates a second order or
  // payment. Forms that handle their own submit with fetch (defaultPrevented) manage themselves.
  const initSubmitLock = () => {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-submit-lock') || event.defaultPrevented) {
        return;
      }
      if (form.dataset.submitLocked === '1') {
        event.preventDefault();
        return;
      }

      form.dataset.submitLocked = '1';
      form.setAttribute('aria-busy', 'true');
      // Disabled only after this tick: a disabled submitter would drop its name/value from the post.
      window.setTimeout(() => {
        form.querySelectorAll('button[type="submit"], input[type="submit"], button:not([type])').forEach((button) => {
          button.disabled = true;
        });
      }, 0);
    });

    // Back navigation from the payment page restores the page from the cache: unlock it.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) {
        return;
      }
      document.querySelectorAll('form[data-submit-lock][data-submit-locked="1"]').forEach((form) => {
        delete form.dataset.submitLocked;
        form.removeAttribute('aria-busy');
        form.querySelectorAll('button[type="submit"], input[type="submit"], button:not([type])').forEach((button) => {
          button.disabled = false;
        });
      });
    });
  };

  ready(() => {
    initSubmitLock();
    initCartDrawer();
    initCustomerTypeFields();
    initPostcodeCityLookup();
    initTicketCopyButtons();
    initPersonalizationForm();
    initCheckoutBillingForm();
    initPaymentCompletionUrl();
  });
})();
